import { NO, UIDialogView, UINativeScrollView, UIRectangle, UITextView, UIView, YES } from "uicore-ts"
import { RHFocusManager } from "../Custom components/RH/Keyboard navigation/RHFocusManager"
import { RHPageFocusManager } from "../Custom components/RH/Keyboard navigation/RHPageFocusManager"
import { RHButton } from "../Custom components/RH/RHButton"
import { RHColor } from "../Custom components/RH/RHColor"
import { RHTextArea } from "../Custom components/RH/RHTextArea"
import { RHButtons, RHDialogs } from "../Custom components/RH/Utils_slop/RHDesignTokens"
import { RHProcurementDetailsViewTokens } from "../Custom components/RH/Utils_slop/RHViewTokens"


/**
 * Confirmation dialog for ending a published procurement or one of its parts
 * without a contract.
 * Modeled on RHAskQuestionDialogView's shape (title, explanation, RHTextArea,
 * submit/cancel buttons) rather than the generic CBDialogViewShower Yes/No
 * dialog, so typing a multi-line reason never fights a global Enter/Arrow-key
 * trap meant for button navigation.
 */
export class RHProcurementClosureDialogView extends UIDialogView {

    readonly contentView = new UINativeScrollView()
        .configuredWithObject({
            backgroundColor: RHDialogs.dialogSurfaceBackgroundColor,
            style: {
                borderRadius: RHDialogs.dialogSurfaceBorderRadius,
                boxShadow: RHDialogs.dialogSurfaceBoxShadow
            }
        })
        .addedAsSubviewToView(this.view)

    readonly titleLabel = new UITextView(undefined, RHDialogs.askQuestionTitleTextViewType)
        .configuredWithObject({
            textColor: RHDialogs.askQuestionTitleTextColor,
            textAlignment: RHDialogs.confirmationQuestionHeadingTextAlignment,
            style: {
                fontFamily: RHDialogs.askQuestionTitleFontFamily,
                fontSize: RHDialogs.askQuestionTitleFontSize,
                fontWeight: RHDialogs.askQuestionTitleFontWeight,
                lineHeight: RHDialogs.askQuestionTitleLineHeight
            }
        })
        .addedAsSubviewToView(this.contentView)

    readonly explanationLabel = new UITextView()
        .configuredWithObject({
            isSingleLine: NO,
            textColor: RHDialogs.askQuestionExplanationTextColor,
            style: {
                fontFamily: RHDialogs.askQuestionExplanationFontFamily,
                fontSize: RHDialogs.askQuestionExplanationFontSize,
                fontWeight: RHDialogs.askQuestionExplanationFontWeight,
                lineHeight: RHDialogs.askQuestionExplanationLineHeight
            }
        })
        .addedAsSubviewToView(this.contentView)

    readonly reasonLabel = new UITextView()
        .configuredWithObject({
            innerHTML: "Lõpetamise põhjus <span style=\"color: " + RHColor.danger.stringValue + "\">*</span>",
            textColor: RHDialogs.askQuestionExplanationTextColor,
            style: {
                fontFamily: RHDialogs.askQuestionExplanationFontFamily,
                fontSize: RHDialogs.askQuestionExplanationFontSize,
                fontWeight: RHDialogs.askQuestionExplanationFontWeight,
                lineHeight: RHDialogs.askQuestionExplanationLineHeight
            }
        })
        .addedAsSubviewToView(this.contentView)

    readonly reasonTextArea = new RHTextArea()
        .configuredWithObject({
            isRequired: YES
        })
        .addedAsSubviewToView(this.contentView)

    readonly reasonValidationMessageLabel = new UITextView()
        .configuredWithObject({
            text: "Lõpetamise põhjus on kohustuslik.",
            textAlignment: UITextView.textAlignment.left,
            textColor: RHColor.danger
        })

    readonly submitButton = new RHButton()
        .configuredWithObject({
            variant: RHButtons.destructiveUtilityVariant,
            enabled: YES,
            contentPadding: RHDialogs.askQuestionFooterButtonContentPadding,
            style: {
                fontSize: RHDialogs.askQuestionFooterButtonFontSize
            },
            controlEventTargetAccumulator: {
                PrimaryActionTriggered: () => {
                    this._confirmClosure()
                }
            }
        })
        .addedAsSubviewToView(this.contentView)

    readonly cancelButton = new RHButton()
        .configuredWithObject({
            titleLabel: { text: "Loobu" },
            variant: RHButtons.exitUtilityVariant,
            contentPadding: RHDialogs.askQuestionFooterButtonContentPadding,
            style: {
                fontSize: RHDialogs.askQuestionFooterButtonFontSize
            },
            controlEventTargetAccumulator: {
                PrimaryActionTriggered: () => {
                    this.dismiss()
                }
            }
        })
        .addedAsSubviewToView(this.contentView)

    override _focusManager = new RHFocusManager(() => [
        this.reasonTextArea,
        this.cancelButton,
        this.submitButton
    ])

    userDidConfirmClosure?: (reason: string) => void

    _bottomView = new UIView().addedAsSubviewToView(this.contentView)


    constructor(elementID?: string) {
        super(elementID)

        this.view = this.contentView
        this.dismissesOnTapOutside = YES

        this.reasonTextArea.addTargetForControlEvent(
            RHTextArea.controlEvent.TextChange, () => this._updateReasonValidationFeedback()
        )
        this.reasonTextArea.controlEventTargetAccumulator.EnterDown = (_sender: UIView, event: Event) => {
            if (event instanceof KeyboardEvent && (event.metaKey || event.ctrlKey)) {
                event.preventDefault()
                this._confirmClosure()
            }
        }

        this.configureForProcurementClosure()
    }


    configureForProcurementClosure() {
        this.titleLabel.text = "Lõpeta ilma lepinguta"
        this.explanationLabel.text = "Ilma lepinguta lõpetatud hanget ei saa taastada. Registreerunud ja " +
            "pakkumuse esitanud pakkujad näevad hanget jätkuvalt lõpetatuna. Pakkumustega ei saa enam toiminguid teha, " +
            "kuid teabevahetus jääb avatuks."
        this.reasonLabel.innerHTML = "Lõpetamise põhjus <span style=\"color: " +
            RHColor.danger.stringValue + "\">*</span>"
        this.reasonTextArea.placeholderText = "Kirjelda, miks hange lõpetatakse ilma lepinguta. Põhjus lisatakse hanke " +
            "teabevahetusse ja saadetakse pakkujatele teavituses."
        this.submitButton.titleLabel.text = "Lõpeta ilma lepinguta"
    }


    configureForProcurementPartClosure(partNumber: number) {
        this.titleLabel.text = `Lõpeta Osa ${partNumber} ilma lepinguta`
        this.explanationLabel.text = "Ilma lepinguta lõpetatud hankeosa ei saa taastada. Sellele osale esitatud " +
            "pakkumustega ei saa enam toiminguid teha. " +
            "Hanke ülejäänud osad jätkuvad tavapäraselt."
        this.reasonLabel.innerHTML = "Lõpetamise põhjus <span style=\"color: " +
            RHColor.danger.stringValue + "\">*</span>"
        this.reasonTextArea.placeholderText = "Kirjelda, miks hankeosa lõpetatakse ilma lepinguta. Põhjus lisatakse hanke " +
            "teabevahetusse ja saadetakse selle osa pakkujatele teavituses."
        this.submitButton.titleLabel.text = `Lõpeta Osa ${partNumber} ilma lepinguta`
    }


    override showInRootView(animated: boolean) {
        super.showInRootView(animated)
        RHPageFocusManager.shared.pushManagerAsNewLayer(this._focusManager)
    }

    override dismiss(animated?: boolean) {
        RHPageFocusManager.shared.popLayer()
        super.dismiss(animated)
    }

    override willAppear(animated?: boolean) {
        super.willAppear(animated)
        this.reasonTextArea.text = ""
        this._updateReasonValidationMessageVisibility(NO)
        this.reasonTextArea.focus()
    }


    _updateReasonValidationFeedback() {
        if (this.reasonTextArea.text.trim().length > 0) {
            this.reasonTextArea.validate()
            this._updateReasonValidationMessageVisibility(NO)
        }
    }


    _updateReasonValidationMessageVisibility(isVisible: boolean) {
        const wasVisible = this.reasonValidationMessageLabel.superview === this.contentView
        if (isVisible) {
            this.reasonValidationMessageLabel.addedAsSubviewToView(this.contentView)
        }
        else if (wasVisible) {
            this.reasonValidationMessageLabel.removeFromSuperview()
        }
        if (wasVisible !== isVisible) {
            this.setNeedsLayoutUpToRootView()
        }
    }

    _confirmClosure() {
        const reason = this.reasonTextArea.text.trim()
        if (reason.length === 0) {
            this.reasonTextArea.validate()
            this._updateReasonValidationMessageVisibility(YES)
            this.reasonTextArea.focus()
            return
        }
        this._updateReasonValidationMessageVisibility(NO)
        this.userDidConfirmClosure?.(reason)
    }


    _footerButtonWidth(button: RHButton): number {
        return Math.min(
            RHDialogs.askQuestionFooterButtonMaxWidth,
            Math.max(
                RHDialogs.askQuestionFooterButtonMinWidth,
                button.titleLabel.intrinsicContentWidth(RHDialogs.askQuestionFooterButtonHeight) +
                RHDialogs.askQuestionFooterButtonContentPadding * 2 +
                RHDialogs.askQuestionFooterButtonTextSafetyBuffer
            )
        )
    }


    override layoutSubviews() {
        super.layoutSubviews()

        const dialogBounds = this.contentBounds
        const dialogWidth = Math.min(
            RHProcurementDetailsViewTokens.closureDialog.maxWidth,
            dialogBounds.width * RHProcurementDetailsViewTokens.closureDialog.widthViewportRatio
        )

        this.contentView.frame = dialogBounds
            .rectangleWithWidth(dialogWidth, 0.5)
            .rectangleWithHeight(0)

        const bounds = this.contentView.contentBoundsWithInset(RHDialogs.askQuestionContentInset)

        let submitButtonWidth = this._footerButtonWidth(this.submitButton)
        let cancelButtonWidth = this._footerButtonWidth(this.cancelButton)
        let footerButtonsRowWidth =
            submitButtonWidth +
            cancelButtonWidth +
            RHDialogs.askQuestionFooterButtonGap

        if (footerButtonsRowWidth > bounds.width) {
            const compactButtonWidth = Math.max(
                0,
                (bounds.width - RHDialogs.askQuestionFooterButtonGap) * 0.5
            )
            submitButtonWidth = compactButtonWidth
            cancelButtonWidth = compactButtonWidth
            footerButtonsRowWidth = bounds.width
        }

        const reasonTextAreaFrame = bounds
            .rectangleWithHeight(this.titleLabel)
            .assignedAsFrameOfView(this.titleLabel)
            .rectangleForNextRow(RHDialogs.askQuestionTitleBottomGap, this.explanationLabel)
            .assignedAsFrameOfView(this.explanationLabel)
            .rectangleForNextRow(RHDialogs.askQuestionExplanationBottomGap, this.reasonLabel)
            .assignedAsFrameOfView(this.reasonLabel)
            .rectangleForNextRow(RHDialogs.askQuestionAccordionBottomGap * 0.5, RHDialogs.askQuestionTextAreaHeight)
            .assignedAsFrameOfView(this.reasonTextArea)

        reasonTextAreaFrame
            .IF(this.reasonValidationMessageLabel.superview === this.contentView)
            .rectangleForNextRow(RHDialogs.askQuestionAccordionBottomGap * 0.5, this.reasonValidationMessageLabel)
            .assignedAsFrameOfView(this.reasonValidationMessageLabel)
            .ENDIF()
            .rectangleForNextRow(RHDialogs.askQuestionFooterButtonsTopGap, RHDialogs.askQuestionFooterButtonHeight)
            .rectangleWithWidth(footerButtonsRowWidth, 0.5)
            .distributeViewsAlongWidth(
                [this.cancelButton, this.submitButton],
                undefined,
                RHDialogs.askQuestionFooterButtonGap,
                [cancelButtonWidth, submitButtonWidth]
            )
            .rectangleForNextRow(0, RHDialogs.askQuestionFooterScrollBottomPadding)
            .assignedAsFrameOfView(this._bottomView)

        const finalHeight = Math.min(
            this._bottomView.frame.max.y,
            dialogBounds.height - RHDialogs.askQuestionContentInset * 2
        )
        this.contentView.frame = dialogBounds
            .rectangleWithWidth(dialogWidth, 0.5)
            .rectangleWithHeight(finalHeight, 0.5)

    }

    override setFrame(rectangle: UIRectangle & { zIndex?: number }, zIndex?: number, performUncheckedLayout?: boolean) {
        super.setFrame(rectangle, zIndex, performUncheckedLayout)
    }

}
